import { useMemo } from 'react';
import { Box, Typography, alpha, Stack, Chip, ToggleButton, ToggleButtonGroup, IconButton, Tooltip } from '@mui/material';
import { MyLocation, Navigation, Whatshot, Visibility, VisibilityOff } from '@mui/icons-material';
import { env } from '../../config/env';
import GoogleMapPanel from '../maps/GoogleMapPanel';
import DispatchRouteMap, { type RouteMapMarker } from './DispatchRouteMap';
import type { FleetMarker, DispatchHeatMapRange } from '../../services/dispatcherService';
import type { DispatchRow } from './dispatchTypes';
import { isLiveTrip } from './dispatchTypes';
import { DISPATCH } from './dispatchTheme';

const MARKER_COLORS: Record<string, string> = {
  driver: '#6366f1',
  assigned_driver: '#2563eb',
  delivery_partner: '#10b981',
  service_provider: '#0891b2',
  pickup: '#22c55e',
  dropoff: '#ef4444',
  merchant: '#ea580c',
  job: '#7c3aed',
};

const HEAT_RANGE_LABELS: Record<DispatchHeatMapRange, string> = {
  live: 'Live',
  '1h': '1h',
  '6h': '6h',
  '24h': '24h',
  today: 'Today',
};

interface DispatchLiveMapProps {
  selected?: DispatchRow | null;
  fleet?: { drivers: FleetMarker[]; partners: FleetMarker[] };
  heatMapPoints?: { lat: number; lng: number; weight: number }[];
  heatMapRange?: DispatchHeatMapRange;
  onHeatMapRangeChange?: (range: DispatchHeatMapRange) => void;
  showHeatMap?: boolean;
  onToggleHeatMap?: () => void;
  totalDemand?: number;
  heatMapLoading?: boolean;
  height?: number | string;
}

function jobMarkers(selected: DispatchRow): RouteMapMarker[] {
  const list: RouteMapMarker[] = [];
  if (selected.pickupLat != null && selected.pickupLng != null) {
    list.push({
      id: 'pickup', lat: selected.pickupLat, lng: selected.pickupLng,
      label: `Pickup · #${selected.ref}`, color: MARKER_COLORS.pickup, kind: 'pickup',
    });
  }
  if (selected.dropoffLat != null && selected.dropoffLng != null) {
    list.push({
      id: 'dropoff', lat: selected.dropoffLat, lng: selected.dropoffLng,
      label: `Drop · #${selected.ref}`, color: MARKER_COLORS.dropoff, kind: 'dropoff',
    });
  }
  return list;
}

function findAssignedMarker(
  selected: DispatchRow,
  fleet?: { drivers: FleetMarker[]; partners: FleetMarker[] },
): FleetMarker | undefined {
  if (!selected.partnerId || !fleet) return undefined;
  const all = [...(fleet.drivers || []), ...(fleet.partners || [])];
  return all.find((m) => m.id === selected.partnerId);
}

export default function DispatchLiveMap({
  selected,
  fleet,
  heatMapPoints = [],
  heatMapRange = 'live',
  onHeatMapRangeChange,
  showHeatMap = true,
  onToggleHeatMap,
  totalDemand,
  heatMapLoading,
  height = '100%',
}: DispatchLiveMapProps) {
  const useGoogle = Boolean(env.googleMapsApiKey);
  const live = selected ? isLiveTrip(selected) : false;
  const assigned = selected ? findAssignedMarker(selected, fleet) : undefined;

  const { center, zoom, markers, route } = useMemo(() => {
    const markersList: RouteMapMarker[] = [];

    (fleet?.drivers || []).forEach((d, i) => {
      if (d.lat == null || d.lng == null) return;
      const isAssigned = selected?.partnerId === d.id;
      markersList.push({
        id: isAssigned ? `assigned-${d.id}` : `driver-${i}`,
        lat: d.lat,
        lng: d.lng,
        label: isAssigned ? `${d.name} · Assigned` : `${d.name} · ${d.status}`,
        color: isAssigned ? MARKER_COLORS.assigned_driver : (d.isAvailable ? MARKER_COLORS.driver : DISPATCH.warning),
        kind: isAssigned ? 'pickup' : 'other',
      });
    });

    (fleet?.partners || []).forEach((p, i) => {
      if (p.lat == null || p.lng == null) return;
      const isAssigned = selected?.partnerId === p.id;
      markersList.push({
        id: isAssigned ? `assigned-${p.id}` : `partner-${i}`,
        lat: p.lat,
        lng: p.lng,
        label: isAssigned ? `${p.name} · Assigned` : p.name,
        color: isAssigned ? MARKER_COLORS.assigned_driver : (MARKER_COLORS[p.type] || '#64748b'),
        kind: isAssigned ? 'pickup' : 'other',
      });
    });

    const jobPts = selected ? jobMarkers(selected) : [];
    markersList.push(...jobPts);

    const routePts: { lat: number; lng: number }[] = [];
    if (assigned?.lat != null && assigned?.lng != null) {
      routePts.push({ lat: assigned.lat, lng: assigned.lng });
    }
    jobPts.forEach((m) => routePts.push({ lat: m.lat, lng: m.lng }));

    const focusPoints = routePts.length ? routePts : markersList.slice(0, 5).map((m) => ({ lat: m.lat, lng: m.lng }));
    const centerPt = focusPoints[0] || heatMapPoints[0] || undefined;

    return {
      center: centerPt,
      zoom: selected && (jobPts.length || assigned) ? 14 : 12,
      markers: markersList,
      route: routePts.length >= 2 ? routePts : jobPts.length >= 2 ? jobPts.map((m) => ({ lat: m.lat, lng: m.lng })) : [],
    };
  }, [selected, fleet, heatMapPoints, assigned]);

  const googleMarkers = markers.map((m) => ({
    lat: m.lat, lng: m.lng, title: m.label, color: m.color,
  }));

  const heatActive = showHeatMap && heatMapPoints.length > 0;

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: { xs: 320, md: 400 }, position: 'relative', bgcolor: alpha(DISPATCH.bgMuted, 0.5) }}>
      {useGoogle ? (
        <GoogleMapPanel
          height={height}
          center={center}
          zoom={zoom}
          markers={googleMarkers}
          route={route}
          heatMapPoints={heatMapPoints}
          showHeatMap={heatActive}
          showTraffic
        />
      ) : (
        <DispatchRouteMap
          height={height === '100%' ? '100%' : height}
          center={center}
          zoom={zoom}
          markers={markers}
          route={route}
          heatMapPoints={heatMapPoints}
          showHeatMap={heatActive}
          activePin="pickup"
        />
      )}

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 600,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          bgcolor: alpha('#fff', 0.95),
          border: `1px solid ${DISPATCH.border}`,
          boxShadow: DISPATCH.shadow,
          flexWrap: 'wrap',
          maxWidth: 'calc(100% - 24px)',
        }}
      >
        <Whatshot sx={{ fontSize: 18, color: '#db2777' }} />
        <Typography variant="caption" fontWeight={800} sx={{ mr: 0.5 }}>
          Demand
        </Typography>
        {onToggleHeatMap && (
          <Tooltip title={showHeatMap ? 'Hide heat map' : 'Show heat map'}>
            <IconButton size="small" onClick={onToggleHeatMap} sx={{ p: 0.25 }}>
              {showHeatMap ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {onHeatMapRangeChange && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={heatMapRange}
            onChange={(_, v) => v && onHeatMapRangeChange(v as DispatchHeatMapRange)}
            sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: 11, fontWeight: 700, lineHeight: 1.4 } }}
          >
            {(Object.keys(HEAT_RANGE_LABELS) as DispatchHeatMapRange[]).map((key) => (
              <ToggleButton key={key} value={key} disabled={heatMapLoading}>
                {HEAT_RANGE_LABELS[key]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {totalDemand != null && showHeatMap && (
          <Chip
            size="small"
            label={`${totalDemand} requests`}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha('#db2777', 0.1), color: '#be185d' }}
          />
        )}
      </Stack>

      {live && selected && (
        <Chip
          icon={<Navigation sx={{ fontSize: '14px !important' }} />}
          label={`Live · ${selected.partner}`}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 600,
            fontWeight: 800,
            bgcolor: alpha('#fff', 0.95),
            border: `1px solid ${alpha(DISPATCH.success, 0.4)}`,
            color: DISPATCH.success,
            boxShadow: DISPATCH.shadow,
          }}
        />
      )}

      {assigned && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{
            position: 'absolute',
            top: live ? 48 : 12,
            right: 12,
            zIndex: 600,
            px: 1.25,
            py: 0.5,
            borderRadius: 2,
            bgcolor: alpha('#fff', 0.95),
            border: `1px solid ${DISPATCH.border}`,
            boxShadow: DISPATCH.shadow,
          }}
        >
          <MyLocation sx={{ fontSize: 16, color: MARKER_COLORS.assigned_driver }} />
          <Box>
            <Typography variant="caption" fontWeight={800} display="block" lineHeight={1.2}>
              {assigned.name}
            </Typography>
            {assigned.speed != null && (
              <Typography variant="caption" color="text.secondary">{Math.round(assigned.speed)} km/h</Typography>
            )}
          </Box>
        </Stack>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          right: 12,
          zIndex: 500,
          pointerEvents: 'none',
          flexWrap: 'wrap',
          gap: 0.5,
        }}
      >
        {[
          { c: MARKER_COLORS.assigned_driver, l: 'Assigned' },
          { c: MARKER_COLORS.driver, l: 'Drivers' },
          { c: MARKER_COLORS.pickup, l: 'Pickup' },
          { c: MARKER_COLORS.dropoff, l: 'Drop' },
          ...(heatActive ? [{ c: '#db2777', l: 'Demand' }] : []),
        ].map((x) => (
          <Box
            key={x.l}
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: 1.5,
              bgcolor: alpha('#fff', 0.92),
              border: `1px solid ${DISPATCH.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: x.c }} />
            <Typography variant="caption" fontWeight={700}>{x.l}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
